import { Search, SlidersHorizontal } from "lucide-react";
import { Fragment, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CryodPivotTable, type DrillPin } from "@/components/ci/cryod-pivot-table";
import { PillGroup } from "@/components/ci/overview-cells";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCryodFacets, useCryodOverview } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSearchParamSetter } from "@/hooks/use-search-param";
import {
  CRYOD_AGGS,
  CRYOD_FIXED_DIMENSIONS,
  CRYOD_METRICS,
  CRYOD_WINDOWS,
  cryodDimensionLabel,
  cryodValueLabel,
} from "@/lib/ci-filters";
import {
  buildCryodDetailHref,
  OVERVIEW_FILTER_PREFIX,
  parseOverviewFilters,
  pinsToFilterParams,
} from "@/lib/cryod-detail";

const SUB_GROUP_NONE = "__none__";
const FILTER_ALL = "__all__";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CiOverviewPage() {
  usePageTitle("Cryod Overview");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const groupBy = searchParams.get("group") ?? "model_id";
  const subGroupBy = searchParams.get("subgroup") ?? "";
  const metric = searchParams.get("metric") ?? "total_restore_ms";
  const agg = searchParams.get("agg") ?? "p50";
  const window = searchParams.get("window") ?? "30d";
  const search = searchParams.get("q") ?? "";

  const setParam = useSearchParamSetter();

  // Expanded rows are persisted in the URL by stable group key (`expand=<key>`),
  // so a drilled-open view is shareable + survives reload.
  const expandedKeys = useMemo(() => searchParams.getAll("expand"), [searchParams]);
  const handleToggleRow = useCallback(
    (rowId: string) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          const current = next.getAll("expand");
          next.delete("expand");
          const remaining = current.includes(rowId)
            ? current.filter((key) => key !== rowId)
            : [...current, rowId];
          for (const key of remaining) next.append("expand", key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const facetsQuery = useCryodFacets();
  const facets = facetsQuery.data;

  // Group-by / sub-group options = fixed dimensions + discovered extra-tag keys.
  const dimensionOptions = useMemo(() => {
    const extras = (facets?.extra_tag_keys ?? []).map((key) => ({
      label: cryodDimensionLabel(`extra:${key}`),
      value: `extra:${key}`,
    }));
    return [...CRYOD_FIXED_DIMENSIONS, ...extras];
  }, [facets]);

  // Active filters from the overview's f_<dim> search params (see cryod-detail.ts).
  const activeFilters = useMemo(() => parseOverviewFilters(searchParams), [searchParams]);
  const filterStrings = useMemo(() => pinsToFilterParams(activeFilters), [activeFilters]);

  const setFilter = useCallback(
    (dimension: string, value: string) => {
      setParam(`${OVERVIEW_FILTER_PREFIX}${dimension}`, value === FILTER_ALL ? "" : value);
    },
    [setParam],
  );

  // Changing the group dimension clears the sub-group if it would now equal it
  // (the backend rejects group_by == sub_group_by with a 400).
  const setGroup = useCallback(
    (value: string | null) => {
      if (!value) return;
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set("group", value);
        if (next.get("subgroup") === value) next.delete("subgroup");
        return next;
      });
    },
    [setSearchParams],
  );

  const overviewQuery = useCryodOverview({
    group_by: groupBy,
    sub_group_by: subGroupBy || undefined,
    metric,
    agg,
    window,
    filters: filterStrings,
  });

  const rows = useMemo(() => {
    const data = overviewQuery.data ?? [];
    if (!search.trim()) return data;
    const needle = search.trim().toLowerCase();
    return data.filter((row) => row.group_key.toLowerCase().includes(needle));
  }, [overviewQuery.data, search]);

  const metricDef = CRYOD_METRICS.find((entry) => entry.value === metric);
  const unit = metricDef?.unit ?? "ms";
  const metricLabel = metricDef?.label ?? metric;

  // Clicking a row drills into a detail screen scoped to the active filters plus
  // the clicked row's pins.
  const handleDrill = useCallback(
    (pins: DrillPin[]) => {
      navigate(
        buildCryodDetailHref({ filters: filterStrings, pins, view: { metric, agg, window } }),
      );
    },
    [navigate, filterStrings, metric, agg, window],
  );

  const filterableDimensions = Object.keys(facets?.values ?? {});

  return (
    <div className="mx-auto max-w-screen-xl space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Cryod Overview</h1>
        <p className="text-sm text-muted-foreground">
          Break down cryod runs by model, args, driver, engine, deploy, storage engine, and custom
          tags
        </p>
      </div>

      {/* Control bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Group
          </span>
          <Select value={groupBy} onValueChange={setGroup}>
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue>{(value) => cryodDimensionLabel(String(value))}</SelectValue>
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[12rem]">
              {dimensionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground/50">›</span>
          <Select
            value={subGroupBy || SUB_GROUP_NONE}
            onValueChange={(value) =>
              setParam("subgroup", !value || value === SUB_GROUP_NONE ? "" : value)
            }
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue>
                {(value) =>
                  !value || value === SUB_GROUP_NONE
                    ? "No sub-group"
                    : cryodDimensionLabel(String(value))
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[12rem]">
              <SelectItem value={SUB_GROUP_NONE}>No sub-group</SelectItem>
              {dimensionOptions
                .filter((option) => option.value !== groupBy)
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="h-5 w-px bg-border" />

        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Metric
          </span>
          <Select value={metric} onValueChange={(value) => value && setParam("metric", value)}>
            <SelectTrigger className="h-8 w-[170px]">
              <SelectValue>
                {(value) =>
                  CRYOD_METRICS.find((entry) => entry.value === value)?.label ?? String(value)
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[12rem]">
              {CRYOD_METRICS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <PillGroup options={CRYOD_AGGS} value={agg} onChange={(value) => setParam("agg", value)} />
        <PillGroup
          options={CRYOD_WINDOWS}
          value={window}
          onChange={(value) => setParam("window", value)}
        />

        <div className="h-5 w-px bg-border" />

        {filterableDimensions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium shadow-xs hover:bg-accent hover:text-accent-foreground">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filters
              {activeFilters.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 font-mono text-[10px]">
                  {activeFilters.length}
                </Badge>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-[420px] w-56 overflow-y-auto">
              {filterableDimensions.map((dimension, index) => {
                const current =
                  activeFilters.find((filter) => filter.dimension === dimension)?.value ??
                  FILTER_ALL;
                return (
                  <Fragment key={dimension}>
                    {index > 0 && <DropdownMenuSeparator />}
                    {/* DropdownMenuGroup is required: base-ui's GroupLabel reads
                        the group context and throws (#31) without it. */}
                    <DropdownMenuGroup>
                      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        {cryodDimensionLabel(dimension)}
                      </DropdownMenuLabel>
                      <DropdownMenuRadioGroup
                        value={current}
                        onValueChange={(value) => setFilter(dimension, value)}
                      >
                        <DropdownMenuRadioItem value={FILTER_ALL}>All</DropdownMenuRadioItem>
                        {(facets?.values[dimension] ?? []).map((value) => (
                          <DropdownMenuRadioItem key={value} value={value}>
                            {cryodValueLabel(dimension, value)}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuGroup>
                  </Fragment>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="relative ml-auto max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setParam("q", event.target.value, { replace: true })}
            placeholder="Filter groups…"
            className="h-8 pl-8"
          />
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.dimension}
              variant="secondary"
              className="gap-1 font-mono text-[11px]"
            >
              {cryodDimensionLabel(filter.dimension)}:{" "}
              {cryodValueLabel(filter.dimension, filter.value)}
              <button
                type="button"
                onClick={() => setFilter(filter.dimension, FILTER_ALL)}
                className="ml-0.5 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Pivot table */}
      <CryodPivotTable
        rows={rows}
        isLoading={overviewQuery.isLoading}
        groupBy={groupBy}
        subGroupBy={subGroupBy}
        metricLabel={metricLabel}
        metricUnit={unit}
        onDrill={handleDrill}
        expandedKeys={expandedKeys}
        onToggleRow={handleToggleRow}
      />
    </div>
  );
}
